import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Clock, Sun, Moon, Calendar } from 'lucide-react'

const hourlyData = [
  [0, 1, 0, 0, 1, 2, 8, 12, 18, 24, 22, 21, 19, 16, 15, 18, 21, 23, 15, 12, 8, 4, 2, 1], // Monday
  [1, 0, 0, 1, 2, 4, 9, 15, 20, 23, 20, 19, 17, 15, 16, 19, 22, 25, 18, 14, 10, 6, 3, 2], // Tuesday  
  [0, 1, 1, 0, 1, 3, 7, 13, 17, 21, 18, 17, 15, 14, 15, 17, 20, 22, 16, 12, 9, 5, 2, 1], // Wednesday
  [1, 0, 0, 2, 3, 5, 10, 16, 21, 25, 22, 20, 18, 16, 17, 20, 23, 26, 19, 15, 11, 7, 4, 2], // Thursday
  [0, 1, 1, 1, 2, 3, 8, 14, 19, 22, 19, 18, 16, 15, 14, 16, 19, 21, 15, 11, 8, 4, 2, 1], // Friday
  [2, 1, 0, 0, 1, 2, 4, 6, 8, 10, 9, 8, 7, 6, 7, 8, 9, 10, 8, 6, 4, 3, 2, 2], // Saturday
  [1, 1, 1, 0, 0, 1, 3, 5, 7, 9, 8, 7, 6, 5, 6, 7, 8, 9, 7, 5, 3, 2, 1, 1]  // Sunday
]

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const hourLabels = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))

const getIntensity = (value: number) => {
  if (value === 0) return 'bg-gray-100 dark:bg-gray-800'
  if (value <= 5) return 'bg-green-200 dark:bg-green-900/40'
  if (value <= 10) return 'bg-yellow-200 dark:bg-yellow-900/40'
  if (value <= 15) return 'bg-orange-300 dark:bg-orange-900/50'
  if (value <= 20) return 'bg-red-400 dark:bg-red-900/60'
  return 'bg-red-600 dark:bg-red-800'
}

const peakHours = [
  { period: '9 AM - 11 AM', description: 'Morning Peak', intensity: 'high', icon: Sun },
  { period: '2 PM - 4 PM', description: 'Afternoon Peak', intensity: 'high', icon: Sun },
  { period: '2 AM - 6 AM', description: 'Lowest Activity', intensity: 'low', icon: Moon }
]

const dayInsights = [
  { day: 'Tuesday', metric: 'Highest Fraud Day', value: '34.2% rate', color: 'text-red-600' },
  { day: 'Thursday', metric: 'Most Active Day', value: '450 leads', color: 'text-blue-600' },
  { day: 'Sunday', metric: 'Lowest Activity', value: '89 leads', color: 'text-green-600' }
]

export const TimeAnalysis: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Time-Based Fraud Analysis
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Hourly Heatmap */}
        <div>
          <h4 className="font-semibold mb-3">Fraud Activity Heatmap (24h × 7 days)</h4>
          <div className="space-y-1">
            {/* Hour labels */}
            <div className="flex gap-1 ml-10">
              {hourLabels.map((hour, index) => (
                index % 3 === 0 && (
                  <div key={hour} className="w-4 text-xs text-center text-muted-foreground">
                    {hour}
                  </div>
                )
              ))}
            </div>
            
            {/* Heatmap grid */}
            {hourlyData.map((dayData, dayIndex) => (
              <div key={dayIndex} className="flex items-center gap-1">
                <div className="w-8 text-xs text-muted-foreground">
                  {dayLabels[dayIndex]}
                </div>
                <div className="flex gap-0.5">
                  {dayData.map((value, hourIndex) => (
                    <div
                      key={hourIndex}
                      className={`w-3 h-3 rounded-sm ${getIntensity(value)}`}
                      title={`${dayLabels[dayIndex]} ${hourLabels[hourIndex]}:00 - ${value} fraud attempts`}
                    />
                  ))}
                </div>
              </div>
            ))}
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-2 mt-3 text-xs text-muted-foreground">
              <span>Less</span>
              <div className="flex gap-0.5">
                <div className="w-3 h-3 rounded-sm bg-gray-100 dark:bg-gray-800" />
                <div className="w-3 h-3 rounded-sm bg-green-200 dark:bg-green-900/40" />
                <div className="w-3 h-3 rounded-sm bg-yellow-200 dark:bg-yellow-900/40" />
                <div className="w-3 h-3 rounded-sm bg-orange-300 dark:bg-orange-900/50" />
                <div className="w-3 h-3 rounded-sm bg-red-400 dark:bg-red-900/60" />
                <div className="w-3 h-3 rounded-sm bg-red-600 dark:bg-red-800" />
              </div>
              <span>More</span>
            </div>
          </div>
        </div>

        {/* Peak Hours Insights */}
        <div>
          <h4 className="font-semibold mb-3">Peak Activity Periods</h4>
          <div className="grid grid-cols-1 gap-3">
            {peakHours.map((peak, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                <div className={`p-2 rounded ${
                  peak.intensity === 'high' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
                }`}>
                  <peak.icon className={`h-4 w-4 ${
                    peak.intensity === 'high' ? 'text-red-600' : 'text-blue-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-sm">{peak.period}</p>
                  <p className="text-xs text-muted-foreground">{peak.description}</p>
                </div>
                <Badge variant={peak.intensity === 'high' ? 'destructive' : 'secondary'}>
                  {peak.intensity === 'high' ? 'HIGH' : 'LOW'}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Day-of-Week Insights */}
        <div>
          <h4 className="font-semibold mb-3">Daily Patterns</h4>
          <div className="space-y-3">
            {dayInsights.map((insight, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">{insight.day}</p>
                    <p className="text-xs text-muted-foreground">{insight.metric}</p>
                  </div>
                </div>
                <span className={`font-semibold ${insight.color}`}>
                  {insight.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Key Recommendations */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            Optimization Recommendations
          </h4>
          <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
            <li>• Increase monitoring during 9-11 AM and 2-4 PM peak hours</li>
            <li>• Consider maintenance windows during 2-6 AM low activity periods</li>
            <li>• Enable stricter rules on Tuesdays and Thursdays (highest fraud days)</li>
            <li>• Weekend fraud patterns are different - adjust detection algorithms</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}